/**
 * crm-agent-queue-service.ts
 *
 * Evidence-First AI Agent Work Leasing Queue (Sprint CRM-01):
 * - Adopts Comp AI CRM durable work lease pattern (leases with heartbeat TTL).
 * - Enforces atomic single-lease ownership to prevent duplicate concurrent agent work.
 * - Auto-reclaims zombie leases on crash/timeout and escalates to Dead Letter Queue (DLQ) after 3 attempts.
 * - Generates typed RFC 6902 patch suggestions with evidence citations.
 * - Auto-supersedes pending suggestions if the target entity's revision increments.
 */

import type {
  CrmAgentTaskLease,
  CrmSuggestion,
  CrmEvidenceRef,
  CrmPatchOperation,
} from '../crm-contracts.js';

export class CrmAgentQueueService {
  private readonly tasks = new Map<string, CrmAgentTaskLease>();
  private readonly suggestions = new Map<string, CrmSuggestion>();
  private readonly LEASE_TIMEOUT_SECONDS = 60;
  private readonly MAX_ATTEMPTS = 3;

  /**
   * Enqueues an agent task
   */
  public enqueueTask(options: {
    taskId: string;
    tenantId: string;
    targetAggregateType: 'person' | 'organization' | 'opportunity';
    targetAggregateId: string;
    taskType: CrmAgentTaskLease['taskType'];
  }): CrmAgentTaskLease {
    const lease: CrmAgentTaskLease = {
      leaseId: `lease-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      taskId: options.taskId,
      tenantId: options.tenantId,
      targetEntityRef: {
        aggregateType: options.targetAggregateType,
        aggregateId: options.targetAggregateId,
      },
      agentPrincipalId: '',
      taskType: options.taskType,
      leasedAt: new Date().toISOString(),
      expiresAt: new Date().toISOString(),
      attemptCount: 0,
      maxAttempts: this.MAX_ATTEMPTS,
      status: 'timed_out', // initially unleased / available
    };

    this.tasks.set(options.taskId, lease);
    return lease;
  }

  /**
   * Acquires an atomic lease on a task
   */
  public acquireLease(
    taskId: string,
    agentPrincipalId: string,
    now: Date = new Date()
  ): { success: boolean; lease?: CrmAgentTaskLease; error?: string } {
    const task = this.tasks.get(taskId);
    if (!task) {
      return { success: false, error: 'ERR_TASK_NOT_FOUND' };
    }

    // Check if actively leased by another agent without expiry
    if (task.status === 'active' && now.getTime() < new Date(task.expiresAt).getTime()) {
      return { success: false, error: 'ERR_TASK_ALREADY_LEASED' };
    }

    if (task.attemptCount >= task.maxAttempts) {
      (task as any).status = 'abandoned';
      return { success: false, error: 'ERR_TASK_EXCEEDED_MAX_ATTEMPTS_DLQ' };
    }

    const expiresAt = new Date(now.getTime() + this.LEASE_TIMEOUT_SECONDS * 1000).toISOString();

    const updatedLease: CrmAgentTaskLease = {
      ...task,
      agentPrincipalId,
      leasedAt: now.toISOString(),
      expiresAt,
      attemptCount: task.attemptCount + 1,
      status: 'active',
    };

    this.tasks.set(taskId, updatedLease);
    return { success: true, lease: updatedLease };
  }

  /**
   * Heartbeat to extend lease TTL
   */
  public renewHeartbeat(taskId: string, agentPrincipalId: string, now: Date = new Date()): boolean {
    const task = this.tasks.get(taskId);
    if (!task || task.status !== 'active' || task.agentPrincipalId !== agentPrincipalId) {
      return false;
    }

    const expiresAt = new Date(now.getTime() + this.LEASE_TIMEOUT_SECONDS * 1000).toISOString();
    (task as any).expiresAt = expiresAt;
    return true;
  }

  /**
   * Agent submits an evidence-grounded suggestion
   */
  public submitSuggestion(options: {
    tenantId: string;
    targetAggregateType: 'person' | 'organization' | 'opportunity';
    targetAggregateId: string;
    targetAggregateRevision: number;
    proposedPatches: readonly CrmPatchOperation[];
    reasoningText: string;
    evidenceRefs: readonly CrmEvidenceRef[];
    confidenceRating?: number;
  }): { success: boolean; suggestion?: CrmSuggestion; error?: string } {
    // Evidence requirement check: must have at least one verified evidence ref
    if (!options.evidenceRefs || options.evidenceRefs.length === 0) {
      return {
        success: false,
        error: 'ERR_EVIDENCE_REQUIRED: Agent suggestions must cite at least one verified evidence reference digest.',
      };
    }

    const suggestionId = `sug-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
    const suggestion: CrmSuggestion = {
      suggestionId,
      tenantId: options.tenantId,
      targetAggregateType: options.targetAggregateType,
      targetAggregateId: options.targetAggregateId,
      targetAggregateRevision: options.targetAggregateRevision,
      proposedPatches: options.proposedPatches,
      reasoningText: options.reasoningText,
      confidenceRating: options.confidenceRating ?? 0.85,
      evidenceRefs: options.evidenceRefs,
      requiresHumanReview: true,
      status: 'pending',
      createdAt: new Date().toISOString(),
    };

    this.suggestions.set(suggestionId, suggestion);
    return { success: true, suggestion };
  }

  /**
   * Auto-supersedes stale suggestions when target entity revision increments
   */
  public invalidateStaleSuggestions(targetAggregateId: string, currentRevision: number): number {
    let count = 0;
    for (const s of this.suggestions.values()) {
      if (s.targetAggregateId === targetAggregateId && s.status === 'pending' && s.targetAggregateRevision < currentRevision) {
        (s as any).status = 'superseded';
        count++;
      }
    }
    return count;
  }

  /**
   * Operator accepts or rejects suggestion
   */
  public reviewSuggestion(
    suggestionId: string,
    decision: 'accepted' | 'rejected',
    reviewerPrincipalId: string
  ): { success: boolean; suggestion?: CrmSuggestion; error?: string } {
    const s = this.suggestions.get(suggestionId);
    if (!s) return { success: false, error: 'ERR_SUGGESTION_NOT_FOUND' };
    if (s.status !== 'pending') return { success: false, error: `ERR_SUGGESTION_ALREADY_${s.status.toUpperCase()}` };

    (s as any).status = decision;
    (s as any).reviewedByPrincipalId = reviewerPrincipalId;
    (s as any).reviewedAt = new Date().toISOString();

    return { success: true, suggestion: s };
  }

  public getSuggestion(suggestionId: string): CrmSuggestion | undefined {
    return this.suggestions.get(suggestionId);
  }

  public listPendingSuggestions(targetAggregateId?: string): readonly CrmSuggestion[] {
    return Array.from(this.suggestions.values()).filter(
      (s) => s.status === 'pending' && (!targetAggregateId || s.targetAggregateId === targetAggregateId)
    );
  }
}

export const globalCrmAgentQueueService = new CrmAgentQueueService();
